import AgoraRTM, { RtmChannel } from 'agora-rtm-sdk'
import { isEmpty } from 'lodash'
import { useCallback } from 'react'

import { useConferenceCall } from './useCall'
import { CallActionSignature } from 'lib/_types'

export const AgoraAppId = process.env.AGORA_APP_ID

export const rtcClient = window.AgoraRTC.createClient({
  mode: 'rtc',
  codec: 'vp8',
})

export const rtmClient = isEmpty(AgoraAppId)
  ? null
  : AgoraRTM.createInstance(AgoraAppId)

const createLazyRTMChannel = () => {
  let channel: RtmChannel

  function createClosure(channelId: string) {
    if (!channel && channelId) {
      channel = rtmClient?.createChannel?.(channelId)
    }

    return channel
  }

  return (channelId: string) => createClosure(channelId)
}

export const useRTMChannel = createLazyRTMChannel()

export const useRTM = () => {
  const conferenceCall = useConferenceCall()

  const channel = useRTMChannel(conferenceCall?.agora_channel)

  const sendChannelMessage = useCallback(
    (text: string) => channel.sendMessage({ text }),
    [channel]
  )

  const sendChannelSignatureMessage = useCallback(
    (command: string, signature?: CallActionSignature) => {
      if (!signature) return

      return sendChannelMessage(
        `${command} ${signature.receiver_uuid} ${signature.timestamp} ${signature.signature}`
      )
    },
    [channel]
  )

  const getChannelAttributes = useCallback(
    () => rtmClient?.getChannelAttributes?.(conferenceCall?.agora_channel),
    [channel]
  )

  const setChannelAttributes = useCallback(
    (attributes: Record<string, string>) =>
      rtmClient?.setChannelAttributes?.(channel.channelId, attributes, {
        enableNotificationToChannelMembers: true,
      }),
    [channel]
  )

  const deleteChannelAttribute = useCallback(
    (attributeKey: string) =>
      rtmClient?.deleteChannelAttributesByKeys?.(
        channel.channelId,
        [attributeKey],
        {
          enableNotificationToChannelMembers: true,
        }
      ),
    [channel]
  )

  return {
    client: rtmClient,
    channel,
    sendChannelMessage,
    sendChannelSignatureMessage,
    getChannelAttributes,
    setChannelAttributes,
    deleteChannelAttribute,
  }
}
